/**
 * Session State Manager
 *
 * Manages persistence and recovery of verification session state
 * to enable graceful handling of browser refresh and navigation.
 * Uses sessionStorage for temporary persistence during browser session.
 */

import type { ScreenDataRecord } from '../types/sessionState';
import type {
  VerificationResult,
  DocumentValidationResult,
  CaptureProgress,
} from '../types/verification';
import type { BrandConfig } from '../contexts/BrandContext';
import { sanitizeVerificationSessionStateForClient } from './verificationResultSanitizer';

export interface VerificationSessionState {
  version: string;
  timestamp: number;
  sessionId: string;

  // Screen flow state
  screenFlow: {
    currentScreen: string;
    screenHistory: string[];
    screenData: ScreenDataRecord;
    progress: number; // 0-100 percentage
    completedSteps: string[]; // Track which screens have been successfully completed
  };

  // WebSocket connection state
  webSocket: {
    sessionId: string | null;
    wsUrl: string | null;
    mode: 'L1' | 'L2' | null;
    connected: boolean;
    lastMessageTimestamp: number | null;
  };

  // Verification progress state
  verification: {
    captureProgress: CaptureProgress;
    verificationMode: 'L1' | 'L2' | null;
    challengeAge: number | null;
    estimatedAge: number | null;
    verificationResult: VerificationResult | null;
  };

  // Camera state (not including actual video streams)
  camera: {
    permissionGranted: boolean;
    selectedDeviceId: string | null;
    lastError: string | null;
    isActive: boolean;
    facingMode?: 'user' | 'environment';
    constraints?: MediaTrackConstraints;
    permissionTimestamp?: number; // Track when permission was granted
    deviceLabel?: string; // Store device label for recovery
  };

  // Document verification state
  document: {
    capturedImage: string | null; // base64 data URL
    processingStarted: boolean;
    reviewCompleted: boolean;
    validationResult: DocumentValidationResult | null;
  };

  // Configuration and context
  config: {
    apiKey: string | null;
    tenantId: string | null;
    displayMode: 'embedded' | 'standalone';
    returnUrl: string | null;
    cancelUrl?: string | null; // Add cancelUrl for API partner flow
    verificationMode: 'L1' | 'L2' | null;
    challengeAge: number | null;
    faceMatchEnabled?: boolean;
    identityVerificationEnabled?: boolean;
    hasOverrides?: boolean;
    externalUserId?: string;
    sessionToken?: string; // Store sessionToken for API partner flow
    brand?: BrandConfig; // Brand configuration for session recovery
    sandboxMode?: boolean;
    emblemUpgradeRequired?: boolean;
    isMobileQR?: boolean;
  };
}

export interface SessionRecoveryInfo {
  hasRecoverableSession: boolean;
  sessionAge: number; // milliseconds since last save
  lastScreen: string;
  progress: number;
  canReconnectWebSocket: boolean;
  canRestoreCamera: boolean;
}

export class SessionStateManager {
  private static instance: SessionStateManager;
  private readonly STORAGE_KEY = 'safepassage_verification_session';
  private readonly STATE_VERSION = '1.0.0';
  private readonly MAX_SESSION_AGE = 30 * 60 * 1000; // 30 minutes
  private readonly AUTO_SAVE_INTERVAL = 5000; // 5 seconds

  private currentState: VerificationSessionState | null = null;
  private autoSaveTimer: NodeJS.Timeout | null = null;
  private isEnabled = true;
  private listeners: Set<() => void> = new Set();

  private constructor() {
    // Initialize with empty state
    this.currentState = this.createEmptyState();
  }

  public static getInstance(): SessionStateManager {
    if (!SessionStateManager.instance) {
      SessionStateManager.instance = new SessionStateManager();
    }
    return SessionStateManager.instance;
  }

  /**
   * Enable or disable session state persistence
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!enabled) {
      this.stopAutoSave();
      this.clearStoredState();
    } else {
      this.startAutoSave();
    }
  }

  /**
   * Initialize session state
   */
  public initializeSession(
    sessionId: string,
    config: Partial<VerificationSessionState['config']> = {}
  ): void {
    this.currentState = {
      ...this.createEmptyState(),
      sessionId,
      timestamp: Date.now(),
      config: sanitizeVerificationSessionStateForClient({
        config: {
          apiKey: null,
          tenantId: null,
          displayMode: 'standalone',
          returnUrl: null,
          verificationMode: null,
          challengeAge: null,
          identityVerificationEnabled: true,
          sandboxMode: undefined,
          ...config,
        },
      }).config,
    };

    this.saveState();
    this.startAutoSave();
  }

  /**
   * Subscribe to state changes
   */
  public subscribe(callback: () => void): () => void {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback());
  }

  /**
   * Update specific parts of the session state
   */
  public updateState(updates: Partial<VerificationSessionState>): void {
    if (!this.isEnabled || !this.currentState) return;

    const safeUpdates = sanitizeVerificationSessionStateForClient(updates);

    // Deep merge updates
    this.currentState = {
      ...this.currentState,
      ...safeUpdates,
      timestamp: Date.now(),
      screenFlow: {
        ...this.currentState.screenFlow,
        ...safeUpdates.screenFlow,
      },
      webSocket: {
        ...this.currentState.webSocket,
        ...safeUpdates.webSocket,
      },
      verification: {
        ...this.currentState.verification,
        ...safeUpdates.verification,
      },
      camera: {
        ...this.currentState.camera,
        ...safeUpdates.camera,
      },
      document: {
        ...this.currentState.document,
        ...safeUpdates.document,
      },
      config: {
        ...this.currentState.config,
        ...safeUpdates.config,
      },
    };

    // Save immediately for critical updates
    if (this.isCriticalUpdate(safeUpdates)) {
      this.saveState();
    }

    // Notify listeners of state change
    this.notifyListeners();
  }

  /**
   * Get current session state (readonly)
   */
  public getState(): Readonly<VerificationSessionState> | null {
    return this.currentState ? { ...this.currentState } : null;
  }

  /**
   * Get initial state for SSR/hydration
   */
  public getInitialState(): VerificationSessionState {
    return this.createEmptyState();
  }

  /**
   * Save current state to sessionStorage
   */
  public saveState(): void {
    if (!this.isEnabled || !this.currentState) return;

    const stateToSave = {
      ...sanitizeVerificationSessionStateForClient(this.currentState),
      timestamp: Date.now(),
    };

    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(stateToSave));
  }

  /**
   * Load state from sessionStorage
   */
  public loadState(): VerificationSessionState | null {
    if (!this.isEnabled) return null;

    const stored = sessionStorage.getItem(this.STORAGE_KEY);
    if (!stored) return null;

    try {
      const parsedState = JSON.parse(stored) as VerificationSessionState;

      // Validate state version and age
      if (this.isValidStoredState(parsedState)) {
        const safeState = sanitizeVerificationSessionStateForClient(parsedState);
        this.currentState = safeState;
        return { ...safeState };
      } else {
        // State is invalid, clear it
        this.clearStoredState();
        return null;
      }
    } catch {
      // Invalid JSON, clear it
      this.clearStoredState();
      return null;
    }
  }

  /**
   * Whether session storage holds the refresh-continuity copy of this
   * session's bearer. Reads storage directly so the check has no side effects.
   */
  public hasPersistedSessionCredential(sessionId: string, sessionToken: string): boolean {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (!stored) return false;
      const parsed = JSON.parse(stored) as Partial<VerificationSessionState> | null;
      return (
        !!sessionId &&
        !!sessionToken &&
        parsed?.sessionId === sessionId &&
        parsed?.config?.sessionToken === sessionToken
      );
    } catch {
      return false;
    }
  }

  /**
   * Check if there's a recoverable session
   */
  public getRecoveryInfo(): SessionRecoveryInfo {
    const stored = this.loadState();

    if (!stored) {
      return {
        hasRecoverableSession: false,
        sessionAge: 0,
        lastScreen: '',
        progress: 0,
        canReconnectWebSocket: false,
        canRestoreCamera: false,
      };
    }

    const sessionAge = Date.now() - stored.timestamp;
    const isRecoverable = sessionAge < this.MAX_SESSION_AGE;

    return {
      hasRecoverableSession: isRecoverable,
      sessionAge,
      lastScreen: stored.screenFlow.currentScreen,
      progress: stored.screenFlow.progress,
      canReconnectWebSocket:
        isRecoverable && stored.webSocket.sessionId !== null,
      canRestoreCamera: isRecoverable && stored.camera.permissionGranted,
    };
  }

  /**
   * Clear stored session state
   */
  public clearStoredState(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Clear current session and storage
   */
  public clearSession(): void {
    this.stopAutoSave();
    this.currentState = null;
    this.clearStoredState();
  }

  /**
   * Start auto-save timer
   */
  public startAutoSave(): void {
    if (!this.isEnabled || this.autoSaveTimer) return;

    this.autoSaveTimer = setInterval(() => {
      this.saveState();
    }, this.AUTO_SAVE_INTERVAL);
  }

  /**
   * Stop auto-save timer
   */
  public stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  /**
   * Export state for debugging/testing
   */
  public exportState(): string {
    return JSON.stringify(this.currentState, null, 2);
  }

  /**
   * Import state for debugging/testing
   */
  public importState(stateJson: string): boolean {
    try {
      const imported = JSON.parse(stateJson) as VerificationSessionState;
      if (this.isValidStoredState(imported)) {
        this.currentState = sanitizeVerificationSessionStateForClient(imported);
        this.saveState();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Create empty/default state
   */
  private createEmptyState(): VerificationSessionState {
    return {
      version: this.STATE_VERSION,
      timestamp: Date.now(),
      sessionId: '',
      screenFlow: {
        currentScreen: 'MainScreen',
        screenHistory: [],
        screenData: {},
        progress: 0,
        completedSteps: [],
      },
      webSocket: {
        sessionId: null,
        wsUrl: null,
        mode: null,
        connected: false,
        lastMessageTimestamp: null,
      },
      verification: {
        captureProgress: {
          currentZone: 0,
          zoneIndex: 0,
          completedZones: [],
          qualityScores: [],
        },
        verificationMode: null,
        challengeAge: null,
        estimatedAge: null,
        verificationResult: null,
      },
      camera: {
        permissionGranted: false,
        selectedDeviceId: null,
        lastError: null,
        isActive: false,
        facingMode: 'user',
        constraints: undefined,
        permissionTimestamp: undefined,
        deviceLabel: undefined,
      },
      document: {
        capturedImage: null,
        processingStarted: false,
        reviewCompleted: false,
        validationResult: null,
      },
      config: {
        apiKey: null,
        tenantId: null,
        displayMode: 'standalone',
        returnUrl: null,
        verificationMode: null,
        challengeAge: null,
        identityVerificationEnabled: true,
        sandboxMode: undefined,
        emblemUpgradeRequired: false,
        isMobileQR: false,
      },
    };
  }

  /**
   * Validate stored state
   */
  private isValidStoredState(
    state: unknown
  ): state is VerificationSessionState {
    if (!state || typeof state !== 'object') return false;

    const stateObj = state as Record<string, unknown>;

    // Check version compatibility
    if (stateObj.version !== this.STATE_VERSION) {
      return false;
    }

    // Check age
    const timestamp =
      typeof stateObj.timestamp === 'number' ? stateObj.timestamp : 0;
    const age = Date.now() - timestamp;
    if (age > this.MAX_SESSION_AGE) {
      return false;
    }

    // Check required fields
    if (!stateObj.sessionId || !stateObj.screenFlow || !stateObj.webSocket) {
      return false;
    }

    return true;
  }

  /**
   * Check if update requires immediate save
   */
  private isCriticalUpdate(
    updates: Partial<VerificationSessionState>
  ): boolean {
    return !!(
      updates.screenFlow?.currentScreen ||
      updates.webSocket?.connected !== undefined ||
      updates.verification?.verificationResult ||
      updates.document?.capturedImage
    );
  }

  /**
   * Mark a step as completed
   */
  public markStepCompleted(step: string): void {
    if (!this.isEnabled || !this.currentState) return;

    if (!this.currentState.screenFlow.completedSteps.includes(step)) {
      this.currentState.screenFlow.completedSteps.push(step);
      this.saveState();
    }
  }

  /**
   * Check if a step has been completed
   */
  public isStepCompleted(step: string): boolean {
    if (!this.currentState) return false;
    return this.currentState.screenFlow.completedSteps.includes(step);
  }

  /**
   * Get all completed steps
   */
  public getCompletedSteps(): string[] {
    if (!this.currentState) return [];
    return [...this.currentState.screenFlow.completedSteps];
  }

  /**
   * Clear completed steps (for reset)
   */
  public clearCompletedSteps(): void {
    if (!this.isEnabled || !this.currentState) return;

    this.currentState.screenFlow.completedSteps = [];
    this.saveState();
  }

  /**
   * Update camera state with enhanced information for better recovery
   */
  public updateCameraState(cameraUpdates: {
    permissionGranted?: boolean;
    selectedDeviceId?: string;
    facingMode?: 'user' | 'environment';
    constraints?: MediaTrackConstraints;
    deviceLabel?: string;
    lastError?: string;
    isActive?: boolean;
  }): void {
    if (!this.isEnabled || !this.currentState) return;

    const cameraUpdate = {
      ...this.currentState.camera,
      ...cameraUpdates,
    };

    // Set timestamp when permission is granted
    if (cameraUpdates.permissionGranted && !this.currentState.camera.permissionGranted) {
      cameraUpdate.permissionTimestamp = Date.now();
    }

    this.updateState({
      camera: cameraUpdate,
    });
  }

  /**
   * Check if camera permission was recently granted (within last hour)
   */
  public isCameraPermissionFresh(): boolean {
    if (!this.currentState?.camera.permissionGranted || !this.currentState.camera.permissionTimestamp) {
      return false;
    }

    const oneHour = 60 * 60 * 1000;
    const permissionAge = Date.now() - this.currentState.camera.permissionTimestamp;
    return permissionAge < oneHour;
  }

  /**
   * Get camera recovery information
   */
  public getCameraRecoveryInfo(): {
    canRestoreCamera: boolean;
    deviceId: string | null;
    deviceLabel: string | null;
    facingMode: 'user' | 'environment';
    permissionFresh: boolean;
  } {
    if (!this.currentState) {
      return {
        canRestoreCamera: false,
        deviceId: null,
        deviceLabel: null,
        facingMode: 'user',
        permissionFresh: false,
      };
    }

    return {
      canRestoreCamera: this.currentState.camera.permissionGranted && this.isCameraPermissionFresh(),
      deviceId: this.currentState.camera.selectedDeviceId,
      deviceLabel: this.currentState.camera.deviceLabel || null,
      facingMode: this.currentState.camera.facingMode || 'user',
      permissionFresh: this.isCameraPermissionFresh(),
    };
  }

  /**
   * Clear camera state (useful when permission is revoked)
   */
  public clearCameraState(): void {
    if (!this.isEnabled || !this.currentState) return;

    this.updateState({
      camera: {
        permissionGranted: false,
        selectedDeviceId: null,
        lastError: null,
        isActive: false,
        facingMode: 'user',
        constraints: undefined,
        permissionTimestamp: undefined,
        deviceLabel: undefined,
      },
    });
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.stopAutoSave();
    this.currentState = null;
  }
}

// Export singleton instance
export const sessionStateManager = SessionStateManager.getInstance();

// Export convenience functions
export const initializeSession = (
  sessionId: string,
  config?: Partial<VerificationSessionState['config']>
) => sessionStateManager.initializeSession(sessionId, config);
export const updateSessionState = (
  updates: Partial<VerificationSessionState>
) => sessionStateManager.updateState(updates);
export const getSessionState = () => sessionStateManager.getState();
export const getSessionRecoveryInfo = () =>
  sessionStateManager.getRecoveryInfo();
export const clearSession = () => sessionStateManager.clearSession();
export const saveSessionState = () => sessionStateManager.saveState();
export const markStepCompleted = (step: string) =>
  sessionStateManager.markStepCompleted(step);
export const isStepCompleted = (step: string) =>
  sessionStateManager.isStepCompleted(step);
export const getCompletedSteps = () => sessionStateManager.getCompletedSteps();
export const updateCameraState = (cameraUpdates: Parameters<typeof sessionStateManager.updateCameraState>[0]) =>
  sessionStateManager.updateCameraState(cameraUpdates);
export const getCameraRecoveryInfo = () => sessionStateManager.getCameraRecoveryInfo();
export const clearCameraState = () => sessionStateManager.clearCameraState();
